import { Box, Stack } from "@mui/material";
import axios from "axios";
import { useCallback, useState } from "react";
import {
  AutocompleteInput,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  useDataProvider,
  useNotify,
} from "react-admin";
import { FieldValues } from "react-hook-form";
import { SiteContryEnum } from "shared/enums/globalEnums";
import { getRawUrlBase, SiteResourcesEnum } from "shared/enums/resourceMappings";
import CardForm from "./components/CardForm";
import TestFingridCsvGenerationToolbar from "./components/TestFingridCsvGenerationToolbar";
import { buildDagRunPayload, getCurrentTestType } from "./helpers";
import { TestType } from "./types";
import { validateForm } from "./validations";

const TestFingridCsvGeneration = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);

  const transform = useCallback((values: FieldValues) => buildDagRunPayload(values, dataProvider), [dataProvider]);

  const handleSubmit = useCallback(
    async (values: FieldValues) => {
      const data = await transform(values);
      try {
        setIsLoading(true);
        const { status } = await axios({
          method: "post",
          url: `${getRawUrlBase(SiteResourcesEnum.Certifications)}/fingrid/fcr/csv`,
          data,
        });
        if (status >= 200 && status < 300) {
          notify("CSV generation triggered successfully", { type: "success" });
        } else {
          throw new Error("Failed to trigger CSV generation");
        }
      } catch (error) {
        console.error("Error processing CSV generation data:", error);
        notify("Failed to process CSV generation data", { type: "error" });
      } finally {
        setIsLoading(false);
      }
    },
    [notify, transform],
  );

  return (
    <SimpleForm
      validate={validateForm}
      onSubmit={handleSubmit}
      toolbar={<TestFingridCsvGenerationToolbar isLoading={isLoading} />}
      defaultValues={{ type: "fcr_n", lowDroop: 0.927, highDroop: 0.1 }}
    >
      <SelectInput
        source="type"
        choices={[
          { id: "fcr_n", name: "FCR-N" },
          { id: "fcr_d", name: "FCR-D" },
          { id: "afrr", name: "AFRR" },
        ]}
        defaultValue="fcr_n"
        sx={{ mb: 2 }}
        validate={[required()]}
      />
      <ReferenceInput
        source="site_id"
        reference={SiteResourcesEnum.Sites}
        filter={{ country__in: [SiteContryEnum.Finland, SiteContryEnum.Sweden] }}
        sx={{ mb: 2 }}
      >
        <AutocompleteInput optionText="name" fullWidth validate={[required()]} parse={(value) => `${value}`} />
      </ReferenceInput>
      <Stack direction="row" spacing={2} width={"100%"} alignItems={"center"}>
        <NumberInput source="lowDroop" label="Low Droop (en MW)" sx={{ flex: 1 }} validate={[required()]} />
        <NumberInput source="highDroop" label="High Droop (en MW)" sx={{ flex: 1 }} validate={[required()]} />
      </Stack>
      <Box mt={2}>
        <FormDataConsumer>
          {({ formData }) => {
            const currentTests = getCurrentTestType(formData?.type as TestType);
            return (
              <Stack direction="row" width={"100%"} flexWrap={"wrap"} gap={2}>
                {currentTests.map((test, index) => {
                  const basekey = `tests_config.${index}.`;
                  return (
                    <CardForm
                      formData={formData}
                      index={index}
                      basekey={basekey}
                      test={test}
                      key={`${test.key}-${index}`}
                    />
                  );
                })}
              </Stack>
            );
          }}
        </FormDataConsumer>
      </Box>
    </SimpleForm>
  );
};

export default TestFingridCsvGeneration;
